import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import { graphql } from "gatsby";
import Button from "../base/button";
import Image from "../base/image";
import Star from "../icons/star";
import Breadcrumb from "../breadcrumb";

const GoalTrackerBanner = ({ bannerData, forceH1 = true, breadcrumbSettings }) => {
  const { copyTitle, copyBody, backgroundColour, progressAmount, goalAmount, bannerImage, buttonText, buttonLink, buttonType, objectFit } =
    bannerData;

  const bannerClass = "banner--goal-tracker";
  const progressPercent = progressAmount > 0 ? (progressAmount / goalAmount) * 100 : 0;

  const formatNumber = (num) => {
    const str = num?.toString().split(".");
    const decimal = str[1] !== undefined ? "." + str[1] : "";
    return str[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",") + decimal;
  };

  // Memoize the progress JSX
  const progress = useMemo(() => {
    return (
      <div className="banner--goal-tracker__progress">
        <div className="banner--goal-tracker__progress-text">
          <p className="banner--goal-tracker__raised-text">${formatNumber(progressAmount)} raised</p>
          <p className="banner--goal-tracker__goal-text">${formatNumber(goalAmount)} goal</p>
        </div>
        <div className="banner--goal-tracker__progress-bar" style={{ width: progressPercent + "%" }}>
          <Star />
        </div>
      </div>
    );
  }, [progressAmount, goalAmount, progressPercent]);

  const heading = useMemo(() => {
    return copyTitle ? ( forceH1 ? <h1 className={`banner--goal-tracker__copy-heading`}>{copyTitle}</h1> : <h2 className={`banner--goal-tracker__copy-heading`}>{copyTitle}</h2> ) : "";
  }, [copyTitle, forceH1]);

  const body = useMemo(() => {
    return copyBody ? <div className="banner--goal-tracker__copy-text">{copyBody}</div> : "";
  }, [copyBody]);
  
  const button = useMemo(() => {
    return buttonLink && buttonText ? (
      <Button
        href={buttonLink}
        type={`btn-${buttonType ? buttonType : "tertiary-small"}`}
        text={buttonText}
        target="_self"
      />
    ) : (
      ""
    );
  }, [buttonLink, buttonText, buttonType]);
  // This part is ensure the goal tracker banner is alwasy serves as cover or contain.
  //calculate the height of the content part, and give it to the image
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const updateContentHeight = useCallback(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.offsetHeight);
    }
  }, []);

  const updateIsMobile = useCallback(() => {
    setIsMobile(window.innerWidth < 768);
  }, []);

  useEffect(() => {
    // Set initial height and mobile state
    updateContentHeight();
    updateIsMobile();

    // Update on resize
    window.addEventListener("resize", updateContentHeight);
    window.addEventListener("resize", updateIsMobile);

    // Clean up on unmount
    return () => {
      window.removeEventListener("resize", updateContentHeight);
      window.removeEventListener("resize", updateIsMobile);
    };
  }, [updateContentHeight, updateIsMobile]);

  //use width and height from bannerImage to calculate the padding bottom as if the width is 100%, padding-bottom is heigt at the percentage of the width.
  //when object fit, we render the image as it is.
  const wrapperImageStyle = objectFit
    ? { paddingBottom: `${(bannerImage?.gatsbyImageData?.height / bannerImage?.gatsbyImageData?.width) * 50}%` }
    : isMobile
    ? { paddingBottom: `${(bannerImage?.gatsbyImageData?.height / bannerImage?.gatsbyImageData?.width) * 100}%` }
    : { paddingBottom: `${contentHeight ?? 50}px` };

  return (
    <div className={`banner bg-${backgroundColour} ${bannerClass}`}>
      <div className={`banner--wrapper`}>
        <div className="banner--wrapper__content" ref={contentRef}>
          <Breadcrumb settings={breadcrumbSettings} />
          {heading}
          {body}
          {progress}
          {button}
        </div>
        {bannerImage ? (
          <div className={`banner--wrapper__image`} style={wrapperImageStyle}>
            <Image objectFit={objectFit ? "contain" : "cover"} imageData={bannerImage} alt={bannerImage.title} />
            {/* <Image className={`${objectFit ? "contain" : "cover"}`} fluid={bannerImage.fluid} objectPosition="50% 50%" alt={bannerImage.title} /> */}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default GoalTrackerBanner;

export const query = graphql`
  fragment GoalTrackerBanner on ContentfulGoalTrackerBanner {
    objectFit
    copyTitle
    copyBody
    bannerImage {
      ...ImageFixed
    }
    backgroundColour
    progressAmount
    goalAmount
    buttonText
    buttonLink
    buttonType
  }
`;
